import { FieldArray, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useQuery } from "@tanstack/react-query";
import {
  CreateUpdateWorkflow,
  CreateUpdateWorkflowTrigger,
  FilterExpression,
  WorkflowEvent,
  WorkflowTriggerType,
} from "@/types/breeze/Workflow";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import CheckboxField from "@/components/form/CheckboxField";
import ScheduleBuilder from "./ScheduleBuilder";
import FilterBuilder from "./FilterBuilder";
import {
  ACTION_ID_PROXY_KEY,
  emptyTrigger,
  toTriggerFormValues,
  toTriggerRequests,
} from "./workflowTriggerForm";
import { useEntities } from "@/hooks/useCommonQueries";
import { tenantPushService } from "@/services/breeze/TenantPushService";
import { Badge, Box, Button, Card, Code, Flex, IconButton, Text } from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";

interface WorkflowSettingsProps<T extends CreateUpdateWorkflow> {
  workflow: T;
  extraFields?: React.ReactNode;
  extraValidationSchema?: Yup.ObjectSchema<any>;
  isSubmitting: boolean;
  saveButtonText?: string;
  showCancelButton?: boolean;
  onSave: (values: T) => void;
  onClose?: () => void;
}

export default function WorkflowSettings<T extends CreateUpdateWorkflow>({
  workflow,
  extraFields,
  extraValidationSchema,
  isSubmitting,
  saveButtonText = "Save Changes",
  showCancelButton = true,
  onSave,
  onClose,
}: WorkflowSettingsProps<T>) {
  const validationSchema = Yup.object().shape({
    ...(extraValidationSchema?.fields ?? {}),
    enabled: Yup.boolean(),
    triggers: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string().required("Required"),
          enabled: Yup.boolean(),
          config: Yup.object().shape({
            event: Yup.string().when("$type", (_, schema) => schema),
            event_entity: Yup.string(),
            manual_entities: Yup.array().of(Yup.string()),
            cron_expression: Yup.string(),
            api_route: Yup.string(),
          }),
        }),
      )
      .min(1, "At least one trigger is required"),
  });

  const { data: entities } = useEntities();

  return (
    <div className="p-4">
      <Formik
        initialValues={{
          ...workflow,
          triggers: toTriggerFormValues(workflow.triggers),
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await onSave({
            ...values,
            triggers: toTriggerRequests(values.triggers, workflow.triggers),
          });
          onClose?.();
        }}
      >
        {({ values, errors, setFieldValue, isSubmitting: formikSubmitting }) => (
          <Form className="space-y-6">
            {Object.keys(errors).length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <Text color="red" size="2" weight="medium">
                  Please fix the following validation errors:
                </Text>
                <ul className="mt-1 list-disc list-inside">
                  {Object.entries(errors).map(([field, error]) => (
                    <li key={field}>
                      <Text color="red" size="1">
                        {field}:{" "}
                        {typeof error === "string" ? error : "Invalid value"}
                      </Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {extraFields}

            <FieldArray
              name="triggers"
              render={(arrayHelpers) => (
                <Flex direction="column" gap="3">
                  <Flex justify="between" align="center">
                    <Text size="3" weight="medium">
                      Triggers
                    </Text>
                    <Button
                      type="button"
                      variant="soft"
                      size="1"
                      onClick={() => arrayHelpers.push(emptyTrigger())}
                    >
                      <Plus className="w-3 h-3" />
                      Add trigger
                    </Button>
                  </Flex>

                  {values.triggers.map(
                    (trigger: CreateUpdateWorkflowTrigger, index: number) => (
                      <Card key={index} variant="surface">
                        <Box p="3">
                          <Flex direction="column" gap="3">
                            <Flex justify="between" align="center">
                              <Text size="2" weight="medium">
                                Trigger {index + 1}
                              </Text>
                              {values.triggers.length > 1 && (
                                <IconButton
                                  type="button"
                                  variant="ghost"
                                  color="red"
                                  size="1"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              )}
                            </Flex>

                            <SelectField
                              name={`triggers.${index}.type`}
                              label="Trigger Type"
                              required
                              type="string"
                              description="Choose how this workflow is triggered (e.g. an API call, database event, timer schedule, or manual button)"
                            >
                              <SelectField.Item value={WorkflowTriggerType.Api}>
                                API Call
                              </SelectField.Item>
                              <SelectField.Item
                                value={WorkflowTriggerType.Event}
                              >
                                Event
                              </SelectField.Item>
                              <SelectField.Item
                                value={WorkflowTriggerType.Timed}
                              >
                                Timed
                              </SelectField.Item>
                              <SelectField.Item
                                value={WorkflowTriggerType.Manual}
                              >
                                Manual
                              </SelectField.Item>
                            </SelectField>

                            {trigger.type === WorkflowTriggerType.Api && (
                              <InputField
                                name={`triggers.${index}.config.api_route`}
                                label="API Route"
                                required
                                description="The HTTP endpoint path that will trigger this workflow when called (e.g. /trigger-sync)"
                              />
                            )}

                            {trigger.type === WorkflowTriggerType.Event && (
                              <>
                                <SelectField
                                  name={`triggers.${index}.config.event`}
                                  label="Event Type"
                                  required
                                  type="string"
                                  description="Select which database mutation event should trigger this workflow"
                                >
                                  {Object.entries(WorkflowEvent)
                                    .filter(([key]) => key !== "None")
                                    .sort((a, b) => a[1].localeCompare(b[1]))
                                    .map(([key, value]) => (
                                      <SelectField.Item
                                        key={value}
                                        value={value}
                                      >
                                        {key
                                          .replace(/([A-Z])/g, " $1")
                                          .trim()}
                                      </SelectField.Item>
                                    ))}
                                </SelectField>

                                {[
                                  WorkflowEvent.EntityCreated,
                                  WorkflowEvent.EntityUpdated,
                                  WorkflowEvent.EntityDeleted,
                                ].includes(
                                  trigger.config.event as WorkflowEvent,
                                ) && (
                                  <>
                                    <SelectField
                                      name={`triggers.${index}.config.event_entity`}
                                      label="Entity"
                                      required
                                      type="string"
                                      description="The database entity table to watch for the selected event"
                                    >
                                      {entities
                                        ?.filter((e) => !e.is_junction)
                                        .sort((a, b) =>
                                          a.name.localeCompare(b.name),
                                        )
                                        .map((entity) => (
                                          <SelectField.Item
                                            key={entity.id}
                                            value={entity.name}
                                          >
                                            {entity.name}
                                          </SelectField.Item>
                                        ))}
                                    </SelectField>
                                    <FilterBuilder
                                      value={trigger.config.filter ?? null}
                                      onChange={(next) =>
                                        setFieldValue(
                                          `triggers.${index}.config.filter`,
                                          next ?? null,
                                        )
                                      }
                                      entityName={
                                        trigger.config.event_entity ?? ""
                                      }
                                    />
                                  </>
                                )}

                                {trigger.config.event === WorkflowEvent.PushActionTaken && (
                                  <PushActionTakenTriggerConfig
                                    triggerIndex={index}
                                    eventEntity={trigger.config.event_entity ?? ""}
                                    filter={trigger.config.filter ?? null}
                                    onFilterChange={(next) =>
                                      setFieldValue(
                                        `triggers.${index}.config.filter`,
                                        next ?? null,
                                      )
                                    }
                                  />
                                )}
                              </>
                            )}

                            {trigger.type === WorkflowTriggerType.Manual && (
                              <SelectField
                                name={`triggers.${index}.config.manual_entities`}
                                label="Entities"
                                required
                                type="string"
                                options={{ multiple: true }}
                                description="Choose which entity detail pages will show a button to manually trigger this workflow"
                              >
                                {entities?.map((entity) => (
                                  <SelectField.Item
                                    key={entity.id}
                                    value={entity.name}
                                  >
                                    {entity.name}
                                  </SelectField.Item>
                                ))}
                              </SelectField>
                            )}

                            {trigger.type === WorkflowTriggerType.Timed && (
                              <ScheduleBuilder
                                name={`triggers.${index}.config.cron_expression`}
                              />
                            )}

                            <CheckboxField
                              name={`triggers.${index}.enabled`}
                              label="Enabled"
                              description="Toggle this specific trigger on or off"
                            />
                          </Flex>
                        </Box>
                      </Card>
                    ),
                  )}
                </Flex>
              )}
            />

            <CheckboxField
              name="enabled"
              label="Enable Workflow"
              description="Activate or deactivate the entire workflow execution globally"
            />
            <div className="flex justify-end gap-3 pt-4">
              {showCancelButton && (
                <Button
                  variant="outline"
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting || formikSubmitting}
                >
                  Cancel
                </Button>
              )}
              <Button type="submit" loading={isSubmitting || formikSubmitting}>
                {saveButtonText}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

interface PushActionTakenTriggerConfigProps {
  triggerIndex: number;
  eventEntity: string;
  filter: FilterExpression | null;
  onFilterChange: (next: FilterExpression | null) => void;
}

function PushActionTakenTriggerConfig({
  triggerIndex,
  eventEntity,
  filter,
  onFilterChange,
}: PushActionTakenTriggerConfigProps) {
  const { setFieldValue } = useFormikContext<unknown>();
  const { data, isLoading } = useQuery({
    queryKey: ["push-categories"],
    queryFn: () => tenantPushService.getCategories(),
  });
  const groups = (data?.categories ?? []).filter(
    (c) => (c.ios_action_order?.length ?? 0) > 0,
  );
  const selected = groups.find((g) => g.category === eventEntity);
  const currentActionId =
    filter?.field === "action_id" && filter.op === "eq"
      ? (filter.value as string | undefined) ?? ""
      : "";

  return (
    <>
      <SelectField
        name={`triggers.${triggerIndex}.config.event_entity`}
        label="iOS button group"
        type="string"
        description={
          isLoading
            ? "Loading registered groups…"
            : groups.length === 0
              ? "No iOS button groups have been registered. Until the app calls POST /push/categories/sync this trigger can only filter by action id."
              : "Match taps coming from a specific button group, or leave blank to match any group."
        }
      >
        <SelectField.Item value="">Any group</SelectField.Item>
        {groups.map((g) => (
          <SelectField.Item key={g.category} value={g.category}>
            {g.label ? `${g.label} (${g.category})` : g.category}
          </SelectField.Item>
        ))}
      </SelectField>

      <Box mb="2">
        <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
          Action id
        </Text>
        <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
          Match a specific action — the workflow only fires when that button is tapped.
        </Text>
        <SelectField
          name={`triggers.${triggerIndex}.config.${ACTION_ID_PROXY_KEY}`}
          label=""
          type="string"
          onChange={(v) => {
            const next = String(v ?? "");
            // We don't persist this proxy field — instead we write a single-
            // condition Filter expression so the existing event-dispatch path
            // picks it up. Blank = no filter (any action).
            if (!next) {
              onFilterChange(null);
            } else {
              onFilterChange({ field: "action_id", op: "eq", value: next });
            }
            // Mirror onto the proxy so SelectField shows the chosen value.
            setFieldValue(
              `triggers.${triggerIndex}.config.${ACTION_ID_PROXY_KEY}`,
              next,
            );
          }}
        >
          <SelectField.Item value="">Any action</SelectField.Item>
          {(selected?.ios_action_order ?? []).map((id) => (
            <SelectField.Item key={id} value={id}>
              <Flex gap="2" align="center">
                <Code size="1" variant="ghost">{id}</Code>
                <Text size="1" color="gray">
                  {selected?.ios_labels?.[id] ?? id}
                </Text>
              </Flex>
            </SelectField.Item>
          ))}
        </SelectField>
        {currentActionId && !selected && (
          <Badge size="1" color="gray" variant="soft">
            Filtering on <Code size="1" variant="ghost">{currentActionId}</Code>
          </Badge>
        )}
      </Box>
    </>
  );
}
